import moment from 'moment';
import { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import Spinner from 'react-bootstrap/Spinner';

import { loadAnalytics, createAnalyticsPDF } from 'shared/api_request_helpers';
import DateRangePicker from './Analytics/components/DateRangePicker';
import AnalyticsCard from './Analytics/components/AnalyticsCard';
import {
  top5categories, top5listings, colorData, outcomeCategories,
  mergeData, asCollection, responsivePeriods, collectionInPercent,
  INTAKES_COLOR, LIVE_OUTCOMES_COLOR, OTHER_OUTCOMES_COLOR
} from './Analytics/data-helpers';
import { loadFrame } from './Analytics/report-helpers';

const ALL_ORGANIZATION_OPTION = {id: '', label: "All Organizations"};

const dateFormat = (date) => moment(date).format('YYYY-M-D');

const urlByActiveState = (activeOnly, from, to, organization) => {
  const dateRange = activeOnly ? {} : {from: dateFormat(from), to: dateFormat(to)};
  const params = new URLSearchParams({...dateRange, organization_id: organization?.id});
  return `/analytics?${params.toString()}`;
};

const organizationItem = (org) => ({id: org.id, label: org.name});

const organizationOptions = (publicOrganizations) => {
  const publicOrganizationOptions = publicOrganizations.map(organizationItem);
  return [ALL_ORGANIZATION_OPTION, ..._.sortBy(publicOrganizationOptions, ['label'])];
};

const dateParamsFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {from: urlParams.get('from'), to: urlParams.get('to')};
};

export default function AnalyticsOverview({helpText, publicOrganizations, isSupervisor, ...props}) {
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [iframe, setIFrame] = useState(false);

  const [activeOnly, setActiveOnly] = useState(props.activeOnly);
  const [selectedOrganization, setSelectedOrganization] = useState(
    props.organization ? organizationItem(props.organization) : ALL_ORGANIZATION_OPTION
  );
  const [statistics, setStatistics] = useState(props.statistics || {date_range: props.date_range || dateParamsFromURL()});
  const {date_range} = statistics;

  const updateIframeState = (e) => {
    if (e.data) {
      setIFrame(true);
      setActiveOnly(e.data.activeOnly);
      setSelectedOrganization(e.data.selectedOrganization);
      setStatistics(e.data.statistics);
      parent.postMessage({type: "loaded"}, window.location.href);
    }
  }

  useEffect(() => {
    if (window.location.hash === '#report') {
      window.addEventListener("message", updateIframeState);
      return () => window.removeEventListener("message", updateIframeState);
    }
  }, []);

  useEffect(() => {
    if (selectedOrganization?.label && window.location.hash !== '#report') {
      setLoading(true);
      const {from, to} = date_range ?? {};
      const dateParams = activeOnly ? [] : [from, to];
      history.replaceState({}, '', urlByActiveState(activeOnly, from, to, selectedOrganization));
      loadAnalytics(selectedOrganization.id, ...dateParams).then(({data}) => {
        setStatistics(data?.statistics);
        setLoading(false);
      }).catch((error) => console.warn(error));
    }
  }, [selectedOrganization, activeOnly, statistics?.date_range?.from, statistics?.date_range?.to]);

  const updateDateRange = (date_range, name) => {
    setStatistics((statistics) => ({...statistics, date_range}));
    setActiveOnly(name === "Active Listings");
  };

  const updateOrganization = (selected) => {
    if (selected) setSelectedOrganization(selected);
  };

  const generatePdf = async () => {
    setGeneratingPdf(true);
    try {
      const markup = await loadFrame({activeOnly, selectedOrganization, statistics});
      const response = await createAnalyticsPDF(markup);
      window.open(response.data.url);
    } catch (error) {
      console.error(error);
    }
    setGeneratingPdf(false);
  }

  return (
    <div className="mb-4">
      {generatingPdf && (
        <div className="generating-pdf p-2">
          Generating PDF, please wait <Spinner animation="border" variant="primary" />
        </div>
      )}
      <div className="d-flex flex-row flex-wrap my-3 justify-content-start">
        <button className="export-pdf mr-auto" onClick={generatePdf} disabled={loading || statistics === null}>
          Export PDF
        </button>
        {isSupervisor && (
          <div className="analytics-organizations">
            <Typeahead
              id="analytics-organizations"
              inputProps={{id: "analytics-organizations-input", name: "analytics-organizations-input"}}
              placeholder="Select or search for an organization"
              selected={[selectedOrganization]}
              options={organizationOptions(publicOrganizations)}
              onChange={([selected]) => updateOrganization(selected)}
              onInputChange={(string) => setSelectedOrganization(string)}
            />
          </div>
        )}
        <DateRangePicker
          activeOnly={activeOnly}
          dateRange={date_range}
          onDateRangeChange={updateDateRange}
          loading={loading}
          iframe={iframe}
        />
      </div>

      {_.isEmpty(_.omit(statistics, 'date_range')) ? (
        <div className="d-flex flex-row my-5 justify-content-center">
          <span className="pr-1">loading</span>
          <Spinner animation="border" variant="primary" />
        </div>
      ) : <>
        <Row className="row-eq-height">
          <Col md="6">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Listing Views"
              graphType="Historical"
              data={{
                total: statistics.listing_views,
                top_five: statistics.top_listing_views,
                by_day: asCollection(statistics.listing_views_by_day, "Listing Views"),
                dataKey: "Listing Views",
                dataColor: "#2c4f85"
              }}
              dateRange={date_range}
              helpText={helpText}
            />
          </Col>
          <Col md="6">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Number of Shares"
              graphType="Historical"
              data={{
                total: statistics.listing_shares,
                top_five: statistics.top_listing_shares,
                by_day: asCollection(statistics.listing_shares_by_day, "Listing Shares"),
                dataKey: "Listing Shares",
                dataColor: "#c0354c"
              }}
              dateRange={date_range}
              helpText={helpText}
            />
          </Col>
        </Row>

        {!activeOnly && <>
          <Row className="row-eq-height">
            <Col md="12">
              <AnalyticsCard
                activeOnly={activeOnly}
                title="Intakes and Outcomes"
                graphType="Line"
                data={responsivePeriods({
                  values: mergeData({
                    "Intakes": statistics.intakes_by_period_for_wide,
                    "Live Outcomes": statistics.live_outcomes_by_period,
                    "Other Outcomes": statistics.other_outcomes_by_period,
                  }),
                  series: [
                    {key: "Intakes", color: INTAKES_COLOR},
                    {key: "Live Outcomes", color: LIVE_OUTCOMES_COLOR},
                    {key: "Other Outcomes", color: OTHER_OUTCOMES_COLOR}
                  ],
                  period: statistics.period_for_wide
                })}
                className="show-middle-y-label"
                dateRange={date_range}
                helpText={helpText}
              />
            </Col>
          </Row>

          <Row className="row-eq-height">
            <Col md="4">
              <AnalyticsCard
                activeOnly={activeOnly}
                title="Intakes"
                graphType="Bar"
                data={{
                  values: asCollection(statistics.intakes_by_period_for_narrow, "Intakes"),
                  period: statistics.period_for_narrow,
                  dataKey: "Intakes",
                  dataColor: INTAKES_COLOR
                }}
                dateRange={date_range}
                helpText={helpText}
              />
            </Col>
            <Col md="4">
              <AnalyticsCard
                activeOnly={activeOnly}
                title="Live Outcomes"
                graphType="Donut"
                data={outcomeCategories(statistics.outcomes, 'live')}
                dateRange={date_range}
                helpText={helpText}
              />
            </Col>
            <Col md="4">
              <AnalyticsCard
                activeOnly={activeOnly}
                title="Other Outcomes"
                graphType="Donut"
                data={outcomeCategories(statistics.outcomes, 'other')}
                dateRange={date_range}
                helpText={helpText}
              />
            </Col>
          </Row>
        </>}

        <Row className="row-eq-height">
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Average Listing Lifespan"
              graphType="AverageDonut"
              data={{
                inputData: top5listings(statistics.listing_lifespans),
                color: "#6D8D3B",
                top5Label: "Longest Lifespans"
              }}
              helpText={helpText}
            />
          </Col>
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Average Length of Stay"
              graphType="AverageDonut"
              data={{
                inputData: top5listings(statistics.stay_lengths),
                color: "#82224E",
                top5Label: "Longest Stays"
              }}
              helpText={helpText}
            />
          </Col>
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Horse Age"
              graphType="AgeBar"
              data={{
                dataColor: "#2c4f85",
                dataKey: "Total:",
                values: collectionInPercent(statistics.listings_by_age, "Total:")
              }}
              dateRange={date_range}
              helpText={helpText}
            />
          </Col>
        </Row>

        <Row className="row-eq-height">
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Gender"
              graphType="Donut"
              data={top5categories(statistics.genders)}
              dateRange={date_range}
              helpText={helpText}
            />
          </Col>
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Top Breeds"
              graphType="TopTen"
              data={{
                total: statistics.total_relevant_listings,
                values: colorData(statistics.breeds)
              }}
              dateRange={date_range}
              helpText={helpText}
            />
          </Col>
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Top Disciplines"
              graphType="TopTen"
              data={{
                total: statistics.total_relevant_listings,
                values: colorData(statistics.disciplines)
              }}
              dateRange={date_range}
              helpText={helpText}
            />
          </Col>
        </Row>

        <Row className="row-eq-height">
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Adoption Inquiries"
              graphType="Historical"
              data={{
                total: statistics.listing_inquiries,
                dataKey: "Inquiries",
                dataColor: "#1f964e",
                by_day: asCollection(statistics.listing_inquiries_by_day, "Inquiries"),
                by_adoption: statistics.listing_inquiries_by_adoption
              }}
              helpText={helpText}
            />
          </Col>
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="BBP Analysis"
              graphType="Historical"
              data={{
                total: statistics.bbp_analysis,
                dataKey: "BBP Analysis",
                dataColor: "#0A6F83",
                by_day: asCollection(statistics.bbp_analysis_by_day, "BBP Analysis"),
                by_adoption: statistics.bbp_analysis_by_adoption,
                tooltipLabel: "Assessment"
              }}
              helpText={helpText}
            />
          </Col>
          <Col md="4">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Microchipped"
              graphType="Historical"
              data={{
                total: statistics.microchipped,
                dataKey: "Microchipped",
                dataColor: "#66326B",
                by_day: asCollection(statistics.microchipped_by_day, "Microchipped"),
                by_adoption: statistics.microchipped_by_adoption
              }}
              helpText={helpText}
            />
          </Col>
        </Row>
        <Row className="row-eq-height">
          <Col md="4" className="bottom-row">
            <AnalyticsCard
              activeOnly={activeOnly}
              title="Fostering Inquiries"
              graphType="Historical"
              data={{
                total: statistics.foster_listing_inquiries,
                dataKey: "Fostering Inquiries",
                dataColor: "#0C6F84",
                by_day: asCollection(statistics.foster_listing_inquiries_by_day, "Fostering Inquiries"),
              }}
              helpText={helpText}
            />
          </Col>
        </Row>
      </>}
    </div>
  );
}
